import { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Toaster } from '../components/ui/sonner';
import { NotificationDropdown } from '../components/layout/NotificationDropdown';
import OnboardingWizard from '../components/OnboardingWizard';
import {
  LayoutDashboard, Users, Share2, Search, Compass, GraduationCap,
  Calendar, Gift, Cpu, User, LogOut, Menu, X, ChevronRight, ChevronDown,
  Send, Sparkles, Briefcase, Handshake, TrendingUp,
  MessageSquare, UserPlus, ArrowRight, Plus,
  FolderSearch, UsersRound, Clock, MapPin, Settings,
  Target, PenLine, FileText, Sunrise, Bot, Home
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/images/area-logo.png";

const sidebarLinks = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Community', href: '/community', icon: MessageSquare },
  { name: 'Partner Dashboard', href: '/dashboard/partner', icon: Handshake },
  { name: 'Referrals', href: '/referrals', icon: Share2 },
  { name: 'Directory', href: '/dashboard/directory', icon: Users },
  { name: 'Opportunities', href: '/opportunities', icon: Compass },
  { name: 'Education', href: '/education', icon: GraduationCap },
  { name: 'Events', href: '/events', icon: Calendar },
  { name: 'Benefits', href: '/benefits', icon: Gift },
  { name: 'Moxey OS', href: '/dashboard/moxey', icon: Cpu },
  { name: 'Split Report', href: '/split-report', icon: Home, highlight: true },
  { name: 'Profile', href: '/dashboard/profile', icon: User },
];

/* ═══════════════ Dashboard Home ═══════════════ */

const quickAiActions = [
  { label: 'Find leads', icon: Target },
  { label: 'Write follow-up', icon: PenLine },
  { label: 'Create post', icon: FileText },
  { label: 'Plan my day', icon: Sunrise },
];

const sampleOpportunities = [
  { type: 'Referral', title: 'Buyer relocating to Austin, TX', from: 'Sarah M.', time: '2h', color: '#3B82F6' },
  { type: 'Job', title: 'Listing agent needed — Denver', from: 'Premier Realty', time: '5h', color: '#10B981' },
  { type: 'Partnership', title: 'Title partner wanted in FL', from: 'James K.', time: '1d', color: '#F59E0B' },
  { type: 'Referral', title: 'Seller in San Diego luxury market', from: 'Carla R.', time: '1d', color: '#3B82F6' },
];

const sampleActivity = [
  { icon: Share2, text: 'New referral posted — buyer seeking luxury condo in Miami Beach', time: '1h ago', color: '#3B82F6', bg: '#EFF6FF' },
  { icon: UserPlus, text: '12 new professionals joined AREA this week', time: '3h ago', color: '#10B981', bg: '#ECFDF5' },
  { icon: TrendingUp, text: 'Q1 2026 market report published — review key trends', time: '6h ago', color: '#F59E0B', bg: '#FFFBEB' },
  { icon: Calendar, text: 'Upcoming: "AI in Real Estate" webinar — March 29', time: '1d ago', color: '#8B5CF6', bg: '#F5F3FF' },
  { icon: Handshake, text: 'HomeTitle Insurance confirmed as a new AREA partner', time: '2d ago', color: '#6366F1', bg: '#EEF2FF' },
];

const communityGroups = [
  { name: 'Luxury Market Pros', members: '842', topic: 'Pricing strategies in a shifting market' },
  { name: 'First-Time Buyer Specialists', members: '1.2K', topic: 'FHA loan guide shared by a member' },
  { name: 'Commercial RE Network', members: '567', topic: 'Office-space trends for 2026' },
];

function DashboardHome() {
  const { user } = useAuth();
  const [moxeyInput, setMoxeyInput] = useState('');
  const [moxeyMessages, setMoxeyMessages] = useState([
    { role: 'assistant', text: `Hi ${user?.name?.split(' ')[0] || 'there'} — I'm Moxey, your AI assistant. I can help you draft messages, find leads, analyze market data, and plan your day. What would you like to do?` }
  ]);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [myEvents, setMyEvents] = useState([]);

  const getToken = () => document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];

  useEffect(() => {
    const fetchMyEvents = async () => {
      const token = getToken();
      if (!token) return;
      try {
        const res = await fetch(`${API}/events/me/registered`, { credentials: 'include', headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) { const d = await res.json(); setMyEvents(d.events || []); }
      } catch (e) { /* ignore */ }
    };
    fetchMyEvents();
  }, []);

  const pushMoxey = (input) => {
    if (!input.trim()) return;
    setMoxeyMessages(prev => [
      ...prev,
      { role: 'user', text: input },
      { role: 'assistant', text: "I'm in preview mode right now. Full capabilities — message drafting, market analysis, lead suggestions — are coming soon." }
    ]);
    setMoxeyInput('');
  };

  return (
    <div className="grid grid-cols-12 gap-5" data-testid="dashboard-overview">
      {/* Onboarding — full span */}
      {showOnboarding && (
        <div className="col-span-12">
          <OnboardingWizard onComplete={() => setShowOnboarding(false)} />
        </div>
      )}

      {/* ──── Row 1 ──── */}

      {/* AI Assistant — 7 cols on xl, full on smaller */}
      <div className="col-span-12 xl:col-span-7">
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_16px_rgba(0,0,0,0.04)] rounded-[18px] overflow-hidden bg-white" data-testid="moxey-ai-panel">
          {/* Header */}
          <div className="bg-[#1E293B] px-6 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-[10px] overflow-hidden flex items-center justify-center">
              <img src="/images/moxey-avatar.png" alt="Moxey" className="w-9 h-9 object-cover" />
            </div>
            <div className="flex-1">
              <h3 className="text-[15px] font-semibold text-white leading-tight">Your AI Assistant</h3>
              <p className="text-slate-400 text-xs">Powered by Moxey</p>
            </div>
            <Badge className="bg-white/10 text-slate-300 border-0 text-[10px] font-medium tracking-wide">PREVIEW</Badge>
          </div>

          <CardContent className="p-0">
            {/* Chat area */}
            <div className="px-5 py-4 space-y-3 min-h-[120px] max-h-[200px] overflow-y-auto">
              {moxeyMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 text-[13px] leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#3D4F7A] text-white rounded-2xl rounded-br-lg'
                      : 'bg-slate-100 text-slate-700 rounded-2xl rounded-bl-lg'
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
            </div>

            {/* Quick action buttons */}
            <div className="px-5 pb-3 flex flex-wrap gap-2">
              {quickAiActions.map((a, i) => (
                <button
                  key={i}
                  onClick={() => pushMoxey(a.label)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-[7px] rounded-full
                             border border-slate-200 text-slate-600 bg-white
                             hover:border-[#3D4F7A] hover:text-[#3D4F7A] hover:shadow-sm transition-all"
                  data-testid={`moxey-action-${a.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <a.icon className="w-3.5 h-3.5" />
                  {a.label}
                </button>
              ))}
            </div>

            {/* Input */}
            <div className="px-5 pb-5 flex gap-2">
              <div className="relative flex-1">
                <Input
                  value={moxeyInput}
                  onChange={(e) => setMoxeyInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && pushMoxey(moxeyInput)}
                  placeholder="Ask Moxey anything…"
                  className="h-10 pr-4 bg-slate-50 border-slate-200 rounded-xl text-sm placeholder:text-slate-400
                             focus-visible:ring-1 focus-visible:ring-[#3D4F7A]/40"
                  data-testid="moxey-input"
                />
              </div>
              <Button
                onClick={() => pushMoxey(moxeyInput)}
                size="icon"
                className="h-10 w-10 rounded-xl bg-[#3D4F7A] hover:bg-[#2A3755] flex-shrink-0"
                data-testid="moxey-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Opportunities — 5 cols */}
      <div className="col-span-12 xl:col-span-5">
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_16px_rgba(0,0,0,0.04)] rounded-[18px] bg-white h-full flex flex-col" data-testid="opportunities-panel">
          <div className="px-6 pt-5 pb-3 flex items-center justify-between">
            <h3 className="text-[15px] font-semibold text-slate-900">New Opportunities</h3>
            <Link to="/referrals" className="text-xs text-[#3D4F7A] font-medium hover:underline">View all</Link>
          </div>
          <CardContent className="px-4 pb-4 pt-0 flex-1 flex flex-col">
            <div className="space-y-2 flex-1">
              {sampleOpportunities.map((o, i) => (
                <div key={i}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer group"
                >
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: o.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-slate-800 truncate group-hover:text-[#3D4F7A] transition-colors">{o.title}</p>
                    <p className="text-[11px] text-slate-400">{o.from} · {o.time}</p>
                  </div>
                  <span className="text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-md"
                    style={{ backgroundColor: o.color + '14', color: o.color }}>
                    {o.type}
                  </span>
                </div>
              ))}
            </div>
            <Button variant="outline" className="w-full mt-3 h-9 text-xs border-dashed border-slate-300 text-slate-500 hover:text-[#3D4F7A] hover:border-[#3D4F7A] rounded-xl">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Post an Opportunity
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* ──── Row 2: Activity Feed ──── */}
      <div className="col-span-12">
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_16px_rgba(0,0,0,0.04)] rounded-[18px] bg-white" data-testid="activity-feed">
          <div className="px-6 pt-5 pb-2 flex items-center justify-between">
            <h3 className="text-[15px] font-semibold text-slate-900">Recent Activity</h3>
            <span className="text-[11px] text-slate-400 font-medium">Updated just now</span>
          </div>
          <CardContent className="px-4 pb-4 pt-0">
            <div className="divide-y divide-slate-100">
              {sampleActivity.map((item, i) => (
                <div key={i} className="flex items-center gap-4 px-2 py-3.5 first:pt-1">
                  <div className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: item.bg }}>
                    <item.icon className="w-4 h-4" style={{ color: item.color }} />
                  </div>
                  <p className="flex-1 text-[13px] text-slate-700 leading-snug">{item.text}</p>
                  <span className="text-[11px] text-slate-400 whitespace-nowrap flex-shrink-0">{item.time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ──── Row 3 ──── */}

      {/* Community Highlights */}
      <div className="col-span-12 lg:col-span-6">
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_16px_rgba(0,0,0,0.04)] rounded-[18px] bg-white h-full" data-testid="community-highlights">
          <div className="px-6 pt-5 pb-3 flex items-center justify-between">
            <h3 className="text-[15px] font-semibold text-slate-900">Community Highlights</h3>
            <Link to="/community" className="text-xs text-[#3D4F7A] font-medium hover:underline">Browse</Link>
          </div>
          <CardContent className="px-4 pb-5 pt-0 space-y-2">
            {communityGroups.map((g, i) => (
              <div key={i}
                className="px-4 py-3 rounded-xl bg-slate-50/80 hover:bg-slate-100 transition-colors cursor-pointer group"
              >
                <div className="flex items-center justify-between mb-0.5">
                  <h4 className="text-[13px] font-semibold text-slate-800 group-hover:text-[#3D4F7A] transition-colors">{g.name}</h4>
                  <span className="text-[11px] text-slate-400">{g.members} members</span>
                </div>
                <p className="text-[12px] text-slate-500 leading-snug">{g.topic}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="col-span-12 lg:col-span-6">
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_16px_rgba(0,0,0,0.04)] rounded-[18px] bg-white h-full" data-testid="quick-actions">
          <div className="px-6 pt-5 pb-3">
            <h3 className="text-[15px] font-semibold text-slate-900">Quick Actions</h3>
          </div>
          <CardContent className="px-4 pb-5 pt-0">
            <div className="grid grid-cols-2 gap-3">
              {[
                { name: 'Post Opportunity', icon: Plus, href: '/referrals', bg: '#EFF6FF', color: '#3B82F6' },
                { name: 'Submit Referral', icon: Share2, href: '/referrals', bg: '#ECFDF5', color: '#10B981' },
                { name: 'Browse Directory', icon: FolderSearch, href: '/dashboard/directory', bg: '#FFFBEB', color: '#F59E0B' },
                { name: 'Join a Group', icon: UsersRound, href: '/community', bg: '#F5F3FF', color: '#8B5CF6' },
              ].map((a, i) => (
                <Link key={i} to={a.href}
                  className="flex items-center gap-3 p-4 rounded-[14px] border border-slate-100
                             hover:border-slate-200 hover:shadow-sm transition-all group"
                  data-testid={`quick-action-${a.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0 transition-colors"
                    style={{ backgroundColor: a.bg }}>
                    <a.icon className="w-5 h-5" style={{ color: a.color }} />
                  </div>
                  <span className="text-[13px] font-medium text-slate-700 group-hover:text-slate-900">{a.name}</span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Events (conditional) */}
      {myEvents.length > 0 && (
        <div className="col-span-12">
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_16px_rgba(0,0,0,0.04)] rounded-[18px] bg-white">
            <div className="px-6 pt-5 pb-3 flex items-center justify-between">
              <h3 className="text-[15px] font-semibold text-slate-900">My Upcoming Events</h3>
              <Link to="/events" className="text-xs text-[#3D4F7A] font-medium hover:underline">View all</Link>
            </div>
            <CardContent className="px-4 pb-5 pt-0">
              <div className="space-y-2">
                {myEvents.slice(0, 3).map((ev) => (
                  <div key={ev.event_id} className="flex items-center gap-4 px-3 py-2.5 rounded-xl bg-slate-50/80">
                    <div className="text-center min-w-[44px] bg-[#3D4F7A] text-white rounded-[10px] py-1.5 px-2">
                      <div className="text-[10px] font-bold uppercase leading-none">
                        {new Date(ev.date).toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                      <div className="text-lg font-bold leading-tight">
                        {new Date(ev.date).getDate()}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-[13px] font-medium text-slate-900 truncate">{ev.title}</h4>
                      <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                        <Clock className="w-3 h-3" />
                        {new Date(ev.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        {ev.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {ev.is_virtual ? 'Virtual' : ev.location}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

/* ═══════════════ Opportunities Hub ═══════════════ */

function OpportunitiesPage() {
  return (
    <div className="grid grid-cols-12 gap-5" data-testid="opportunities-page-dashboard">
      {[
        { name: 'Referrals', desc: 'Share and receive business referrals', icon: Share2, href: '/referrals', color: '#3B82F6', bg: '#EFF6FF' },
        { name: 'Jobs', desc: 'Career and team opportunities', icon: Briefcase, href: '/jobs', color: '#10B981', bg: '#ECFDF5' },
        { name: 'Directory', desc: 'Find trusted professionals', icon: Users, href: '/dashboard/directory', color: '#F59E0B', bg: '#FFFBEB' },
        { name: 'Partner Discounts', desc: 'Exclusive savings from partners', icon: Gift, href: '/discounts', color: '#8B5CF6', bg: '#F5F3FF' },
        { name: 'Affiliate Program', desc: 'Earn by growing the alliance', icon: TrendingUp, href: '/affiliate', color: '#6366F1', bg: '#EEF2FF' },
      ].map((item, i) => (
        <Link key={i} to={item.href} className="col-span-12 sm:col-span-6 lg:col-span-4">
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_16px_rgba(0,0,0,0.04)] rounded-[18px] bg-white hover:shadow-lg transition-all h-full group cursor-pointer">
            <CardContent className="p-6 flex items-start gap-4">
              <div className="w-12 h-12 rounded-[12px] flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: item.bg }}>
                <item.icon className="w-6 h-6" style={{ color: item.color }} />
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-slate-900 group-hover:text-[#3D4F7A] transition-colors">{item.name}</h3>
                <p className="text-[13px] text-slate-500 mt-0.5">{item.desc}</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

/* ═══════════════ Floating AI Button ═══════════════ */

function FloatingMoxeyButton({ show }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Hi! I can help you draft messages, find opportunities, or plan your day. What do you need?' }
  ]);

  if (!show) return null;

  const send = () => {
    if (!input.trim()) return;
    setMessages(prev => [
      ...prev,
      { role: 'user', text: input },
      { role: 'assistant', text: "I'm in preview mode. Full Moxey AI capabilities — message drafting, market analysis, smart recommendations — are coming soon." }
    ]);
    setInput('');
  };

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full shadow-lg
                    flex items-center justify-center transition-all
                    ${open ? 'bg-slate-800 rotate-0' : 'bg-gradient-to-br from-indigo-500 to-violet-600 hover:scale-105'}`}
        data-testid="floating-moxey-btn"
      >
        {open ? <X className="w-5 h-5 text-white" /> : <img src="/images/moxey-avatar.png" alt="Moxey" className="w-8 h-8 rounded-full object-cover" />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-20 right-6 z-50 w-[340px] rounded-[18px] shadow-2xl border border-slate-200 bg-white overflow-hidden"
          data-testid="floating-moxey-panel">
          <div className="bg-[#1E293B] px-5 py-3 flex items-center gap-2.5">
            <img src="/images/moxey-avatar.png" alt="Moxey" className="w-5 h-5 rounded-full object-cover" />
            <span className="text-sm font-semibold text-white">Moxey AI</span>
            <Badge className="ml-auto bg-white/10 text-slate-300 border-0 text-[10px]">PREVIEW</Badge>
          </div>
          <div className="px-4 py-3 space-y-2.5 max-h-60 overflow-y-auto">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3.5 py-2 text-[12px] leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-[#3D4F7A] text-white rounded-2xl rounded-br-lg'
                    : 'bg-slate-100 text-slate-700 rounded-2xl rounded-bl-lg'
                }`}>{m.text}</div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-slate-100 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Ask anything…"
              className="h-9 text-xs bg-slate-50 rounded-xl border-slate-200"
            />
            <Button onClick={send} size="icon" className="h-9 w-9 rounded-xl bg-[#3D4F7A] hover:bg-[#2A3755] flex-shrink-0">
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════ Main Dashboard Shell ═══════════════ */

export default function Dashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const { user, logout, loading, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#3D4F7A] border-t-transparent" />
      </div>
    );
  }
  if (!user) return null;

  const isOverview = location.pathname === '/dashboard';
  const isOpportunities = location.pathname === '/opportunities';
  const showFloatingAi = !isOverview; // Hide on dashboard home (AI already visible)

  const renderContent = () => {
    if (isOverview) return <DashboardHome />;
    if (isOpportunities) return <OpportunitiesPage />;
    return <Outlet />;
  };

  const getPageTitle = () => {
    if (isOverview) return 'Dashboard';
    if (isOpportunities) return 'Opportunities';
    const seg = location.pathname.split('/').pop();
    return seg.charAt(0).toUpperCase() + seg.slice(1);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB]" data-testid="dashboard">

      {/* ── Sidebar ── */}
      <aside className={`
        fixed top-0 left-0 z-50 w-[260px] h-screen bg-white border-r border-slate-200/70
        flex flex-col transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-[60px] flex items-center px-6 border-b border-slate-100 flex-shrink-0">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="AREA" className="h-8 w-auto" />
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-5 px-3 space-y-[2px] overflow-y-auto">
          {sidebarLinks.map((link) => {
            const active = location.pathname === link.href ||
              (link.href !== '/dashboard' && location.pathname.startsWith(link.href));
            return (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3.5 py-[9px] rounded-[10px] text-[13px] font-medium transition-all
                  ${active
                    ? 'bg-[#3D4F7A] text-white shadow-sm'
                    : link.highlight
                      ? 'text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                  }
                `}
                data-testid={`sidebar-${link.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <link.icon className="w-[17px] h-[17px]" />
                {link.name}
              </Link>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-slate-100 p-3 space-y-[2px] flex-shrink-0">
          <Link to="/dashboard/settings" onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 px-3.5 py-[9px] rounded-[10px] text-[13px] font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-all"
            data-testid="sidebar-settings">
            <Settings className="w-[17px] h-[17px]" /> Settings
          </Link>
          <button onClick={handleLogout}
            className="flex items-center gap-3 px-3.5 py-[9px] rounded-[10px] text-[13px] font-medium text-red-400 hover:bg-red-50 hover:text-red-600 transition-all w-full"
            data-testid="sidebar-signout">
            <LogOut className="w-[17px] h-[17px]" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* ── Main ── */}
      <div className="lg:pl-[260px] flex flex-col min-h-screen">

        {/* Top bar */}
        <header className="sticky top-0 z-30 h-[60px] bg-white/85 backdrop-blur-lg border-b border-slate-200/50 flex items-center justify-between px-5 lg:px-8">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 -ml-2 rounded-lg text-slate-600 hover:bg-slate-100">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <h1 className="text-base font-semibold text-slate-900 hidden sm:block" data-testid="page-title">{getPageTitle()}</h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="hidden md:flex items-center relative">
              <Search className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input placeholder="Search…" className="w-56 lg:w-64 pl-9 h-9 bg-slate-50/80 border-slate-200 text-[13px] rounded-xl focus-visible:ring-1 focus-visible:ring-[#3D4F7A]/30" data-testid="topbar-search" />
            </div>

            {/* Notifications */}
            <NotificationDropdown />

            {/* Profile Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2.5 pl-3 border-l border-slate-200 cursor-pointer hover:opacity-80 transition-opacity"
                data-testid="dashboard-user-menu-btn"
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback className="bg-[#3D4F7A] text-white text-xs font-semibold">
                    {user.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-[13px] font-medium text-slate-700 hidden lg:block max-w-[120px] truncate">{user.name}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform hidden lg:block ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {userMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-lg shadow-lg border border-slate-200 py-1.5 z-50" data-testid="dashboard-user-dropdown">
                  <div className="px-3.5 py-2 border-b border-slate-100">
                    <p className="font-medium text-sm text-slate-900 truncate">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                  <Link
                    to="/dashboard/profile"
                    className="flex items-center gap-2 px-3.5 py-2 text-sm text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50"
                    onClick={() => setUserMenuOpen(false)}
                    data-testid="dashboard-menu-profile"
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-2 px-3.5 py-2 text-sm text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50"
                      onClick={() => setUserMenuOpen(false)}
                      data-testid="dashboard-menu-admin"
                    >
                      <Settings className="w-4 h-4" />
                      Admin Panel
                    </Link>
                  )}
                  <div className="border-t border-slate-100 mt-1 pt-1">
                    <button
                      onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                      className="flex items-center gap-2 w-full px-3.5 py-2 text-sm text-red-600 hover:bg-red-50"
                      data-testid="dashboard-menu-signout"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 lg:p-8 pb-20 lg:pb-8 max-w-[1400px] w-full mx-auto">
          {renderContent()}
        </main>
      </div>

      {/* Floating AI — visible on sub-pages */}
      <FloatingMoxeyButton show={showFloatingAi} />

      {/* Mobile Bottom Tab Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-white border-t border-slate-200 safe-area-pb" data-testid="mobile-tab-bar">
        <div className="flex items-center justify-around h-14">
          {[
            { name: 'Home', href: '/dashboard', icon: LayoutDashboard },
            { name: 'Community', href: '/community', icon: MessageSquare },
            { name: 'Moxey', href: '/dashboard/moxey', icon: Cpu },
            { name: 'Splits', href: '/split-report', icon: Home },
            { name: 'Profile', href: '/dashboard/profile', icon: User },
          ].map((tab) => {
            const active = location.pathname === tab.href || (tab.href !== '/dashboard' && location.pathname.startsWith(tab.href));
            return (
              <Link
                key={tab.name}
                to={tab.href}
                className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors ${active ? 'text-[#3D4F7A]' : 'text-slate-400 hover:text-slate-600'}`}
                data-testid={`mobile-tab-${tab.name.toLowerCase()}`}
              >
                <tab.icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
                <span className="text-[10px] font-medium">{tab.name}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      <Toaster position="top-right" />
    </div>
  );
}
